import React, { useState, useCallback, useEffect, useRef } from 'react';
import { styles } from '../styles';
import { navLinks } from '../constants';
import { logo, menu, close } from '../assets';

const Navbar = () => {
	const [active, setActive] = useState('');
	const [toggle, setToggle] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const menuRef = useRef(null);

	useEffect(() => {
		const handleScroll = () => setScrolled(window.scrollY > 20);
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	useEffect(() => {
		const sections = navLinks.map(link => document.getElementById(link.id));
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const link = navLinks.find(l => l.id === entry.target.id);
						if (link) setActive(link.title);
					}
				});
			},
			{ rootMargin: '-40% 0px -55% 0px' }
		);
		sections.forEach(s => s && observer.observe(s));
		return () => observer.disconnect();
	}, []);

	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Escape' && toggle) setToggle(false);
	}, [toggle]);

	useEffect(() => {
		if (toggle) {
			document.addEventListener('keydown', handleKeyDown);
			menuRef.current?.querySelector('a')?.focus();
		}
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [toggle, handleKeyDown]);

	return (
		<nav
			className={`${styles.paddingX} w-full flex items-center py-4 fixed top-0 z-20 transition-all duration-300 ${
				scrolled
					? 'bg-warm/80 backdrop-blur-lg border-b border-border shadow-sm'
					: 'bg-transparent'
			}`}
			role="navigation"
			aria-label="Main navigation">
			<div className="w-full flex justify-between items-center max-w-6xl mx-auto">
				<a
					href="#"
					className="flex items-center gap-2"
					onClick={(e) => {
						e.preventDefault();
						setActive('');
						window.scrollTo(0, 0);
					}}
					aria-label="Sachin Saurav - Back to top">
					<img src={logo} alt="" className="w-8 h-8 object-contain" aria-hidden="true" />
					<p className="text-primary text-[16px] font-semibold cursor-pointer">
						Sachin Saurav
					</p>
				</a>
				<ul className="list-none hidden sm:flex flex-row gap-8" role="list">
					{navLinks.map((link) => (
						<li key={link.id}>
							<a
								href={`#${link.id}`}
								className={`text-[15px] font-medium cursor-pointer transition-colors duration-200 relative pb-1 ${
									active === link.title
										? 'text-accent'
										: 'text-muted hover:text-primary'
								}`}
								onClick={() => setActive(link.title)}>
								{link.title}
								{active === link.title && (
									<span className="absolute bottom-0 left-0 w-full h-0.5 bg-accent rounded-full" />
								)}
							</a>
						</li>
					))}
				</ul>
				<div className="sm:hidden flex flex-1 justify-end items-center">
					<button
						type="button"
						className="w-[28px] h-[28px] bg-transparent border-none p-0 cursor-pointer"
						onClick={() => setToggle(!toggle)}
						aria-expanded={toggle}
						aria-controls="mobile-menu"
						aria-label={toggle ? 'Close menu' : 'Open menu'}>
						<img
							src={toggle ? close : menu}
							alt=""
							className="w-full h-full object-contain"
							aria-hidden="true"
						/>
					</button>
					<div
						id="mobile-menu"
						ref={menuRef}
						className={`${
							!toggle ? 'hidden' : 'flex'
						} p-6 bg-warm border border-border shadow-lg absolute top-16 right-4 min-w-[180px] z-10 rounded-xl`}
						role="menu">
						<ul className="list-none flex flex-col gap-4 w-full" role="list">
							{navLinks.map((link) => (
								<li key={link.id} role="menuitem">
									<a
										href={`#${link.id}`}
										className={`text-[15px] font-medium block transition-colors duration-200 ${
											active === link.title
												? 'text-accent'
												: 'text-muted hover:text-primary'
										}`}
										onClick={() => {
											setToggle(false);
											setActive(link.title);
										}}>
										{link.title}
									</a>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</nav>
	);
};

export default Navbar;
